/**
 * Prirodzené radenie kódov menu (1,2,3,...,10; A,B,C...).
 *
 * @param {string|number|null|undefined} codeA
 * @param {string|number|null|undefined} codeB
 * @returns {number}
 */
export function compareMenuCodesNatural(codeA, codeB) {
  const left = codeA == null ? "" : String(codeA);
  const right = codeB == null ? "" : String(codeB);
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Vypočíta zložené menu (polievka + hlavné jedlo = napr. "C + 8") zo zoznamu položiek.
 *
 * @param {Array<{item_type: string, menu_code: string, name: string, quantity: number}>} items
 * @returns {Array<{label: string, count: number, soupCode: string|null, mainCode: string|null, soupName: string, mainName: string}>}
 */
export function getComposedMenus(items) {
  const expanded = [];
  for (const item of items) {
    const qty = item.quantity || 0;
    for (let i = 0; i < qty; i++) {
      expanded.push(item);
    }
  }
  const soups = expanded.filter(i => i.item_type === "SOUP");
  const mains = expanded.filter(i => i.item_type === "MENU");
  const pairCount = Math.min(soups.length, mains.length);
  const labelCounts = {};
  for (let i = 0; i < pairCount; i++) {
    const sc = (soups[i].menu_code || "").trim();
    const mc = (mains[i].menu_code || "").trim();
    const label = `${sc || "?"} + ${mc || "?"}`;
    if (!labelCounts[label]) {
      labelCounts[label] = {
        count: 0,
        soupCode: sc || null,
        mainCode: mc || null,
        soupName: soups[i].name || "",
        mainName: mains[i].name || "",
      };
    }
    labelCounts[label].count++;
  }
  return Object.entries(labelCounts).map(([label, { count, soupCode, mainCode, soupName, mainName }]) => ({
    label, count, soupCode, mainCode, soupName, mainName,
  }));
}

/**
 * Vráti label a príznak štandardnej sadzby pre tag cenníka objednávky (orders.kod_cennik).
 *
 * @param {string|null|undefined} kodCennik
 * @returns {{label: string, isDefault: boolean}}
 */
export function getPriceListTag(kodCennik) {
  const code = String(kodCennik || "").trim();
  const isDefault = !code || code.toUpperCase() === "STANDARD";
  return { label: isDefault ? "ŠTANDARD" : code, isDefault };
}

/**
 * Overi, či dvojica súradníc predstavuje reálne spresnenú polohu doručenia.
 *
 * Súradnice 0,0 ležia uprostred Atlantiku – nikdy to nie je skutočná adresa doručenia,
 * len pozůstatok chybne uloženého "žiadna poloha" (Number(null) === 0).
 *
 * @param {{lat?: number|string|null, lng?: number|string|null}|null|undefined} location
 * @returns {boolean}
 */
export function isValidDeliveryLocation(location) {
  if (location == null || location.lat == null || location.lng == null) {
    return false;
  }
  const lat = Number(location.lat);
  const lng = Number(location.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false;
  }
  return !(lat === 0 && lng === 0);
}

/**
 * Vráti spresnenú polohu adresy/objednávky ako {lat, lng}, alebo null ak nie je nastavená.
 *
 * @param {{latitude?: number|string|null, longitude?: number|string|null}|null|undefined} row
 * @returns {{lat: number, lng: number}|null}
 */
export function getDeliveryLocation(row) {
  const location = { lat: row?.latitude, lng: row?.longitude };
  return isValidDeliveryLocation(location)
    ? { lat: Number(location.lat), lng: Number(location.lng) }
    : null;
}

/**
 * Zostaví odkaz na Google Mapy pre adresu doručenia objednávky.
 * Ak má adresa spresnenú polohu (latitude/longitude), vráti navigáciu na súradnice,
 * inak vyhľadávanie podľa textovej adresy.
 *
 * @param {{latitude?: number|string|null, longitude?: number|string|null, delivery_address?: string|null}} order
 * @returns {{url: string|null, hasPreciseLocation: boolean}}
 */
export function getOrderMapsLink(order) {
  const location = getDeliveryLocation(order);

  if (location) {
    return {
      url: `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`,
      hasPreciseLocation: true,
    };
  }

  const address = (order?.delivery_address || "").trim();
  return {
    url: address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
      : null,
    hasPreciseLocation: false,
  };
}
